import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { useFormik } from "formik";
import { FormikProvider } from 'formik';
import textfieldStyle from "../../GlobalStyles/styles";
import { TextField, CircularProgress } from '@mui/material';
import buttonStyles from '../YesNoButtonsCSS/YesNoButtons.module.css';
import styles from './FeePropFeePopUp.module.css';
import { handleSubmitFee, completeTask, setShowError } from "../../redux/slices/dashboard";
import { useDispatch, useSelector } from "../../redux/store";
import SliderSwitch from '../SliderSwitch/SliderSwitch';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

const FeePropFeePopUp = ({ isVisible, onClose, onSubmit, taskData }) => {
    const { accounts } = useMsal();
    const account = accounts[0];
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Get error state from Redux (we'll handle success locally)
    const { showError, errorMessage } = useSelector((state) => state.dashboard);

    // Handle error notifications only
    useEffect(() => {
        if (showError && errorMessage) {
            enqueueSnackbar(errorMessage, { variant: 'error' });
            dispatch(setShowError(false));
        }
    }, [showError, errorMessage, enqueueSnackbar, dispatch]);

    // Form validation schema using Yup
    const validationSchema = Yup.object().shape({
        fee: Yup.number().min(0).required('Fee is required'),
        // comment: Yup.string().required('Comment is required'),
        suitableRunner: Yup.string().required('Please select a suitable runner/team'),
    });

    const initialValues = {
        fee: '',
        comment: '',
        suitableRunner: 'N/A',
        suitableForRestructuredFollowUp: false,
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsSubmitting(true);
            
            try {
                const modalData = {
                    jobNumber: taskData.jobNumber,
                    fee: values.fee,
                    comment: values.comment,
                    commentForSuitableTeam: values.suitableRunner,
                    suitableForRestructuredFollowUp: values.suitableForRestructuredFollowUp,
                    updatedBy: account.username,
                };

                await dispatch(handleSubmitFee(modalData));
                await dispatch(completeTask(taskData.jobNumber, taskData.deadlineID, taskData.deadlineType, taskData.updatedBy));
                
                // Show success notification directly here to prevent multiple notifications
                enqueueSnackbar('Fee proposal submitted successfully!', { variant: 'success' });
                
                resetForm();
                onClose(); // Close the modal
                console.log(values);
            } catch (error) {
                console.error('Error submitting fee proposal:', error);
                // Error handling is done through Redux state
            } finally {
                setIsSubmitting(false);
            }
        },
    });
    const { getFieldProps, touched, errors, handleSubmit } = formik;

    if (!isVisible) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <FormikProvider value={formik} handleSubmit={handleSubmit}>
                    <p className={styles.text}>Please confirm the fee for this project</p>
                    
                    <div className={styles.commentModal_feeSection}>
                        <TextField
                            value={formik.values.fee}
                            type="number"
                            onChange={(e) =>
                                formik.setFieldValue("fee", e?.target?.value)
                            }
                            placeholder="£"
                            sx={textfieldStyle}
                            error={Boolean(touched.fee && errors.fee)}
                        />
                    </div>
                    
                    <div className={styles.commentModal_commentSection}>
                        <TextField
                            fullWidth
                            value={formik.values.comment}
                            type="text"
                            onChange={(e) =>
                                formik.setFieldValue("comment", e?.target?.value)
                            }
                            multiline
                            rows={2}
                            placeholder="Comment"
                            sx={textfieldStyle}
                        />
                    </div>
                    
                    <p className={styles.sectionLabel}>Please advise on a suitable Team/Runner</p>
                    <div className={styles.commentModal_teamCommentSection}>
                        <TextField
                            fullWidth
                            value={formik.values.suitableRunner}
                            type="text"
                            onChange={(e) =>
                                formik.setFieldValue("suitableRunner", e?.target?.value)
                            }
                            multiline
                            rows={2}
                            placeholder="Comment suitable runner"
                            sx={textfieldStyle}
                            error={Boolean(touched.suitableRunner && errors.suitableRunner)}
                        />
                    </div>
                    
                    <div className={styles.toggleSection}>
                        <p className={styles.toggleLabel}>Suitable for Re-Structured follow up?</p>
                        <div className={styles.toggleControl}>
                            <span className={styles.toggleText}>No</span>
                            <SliderSwitch
                                checked={formik.values.suitableForRestructuredFollowUp}
                                onToggle={(checked) => formik.setFieldValue("suitableForRestructuredFollowUp", checked)}
                            />
                            <span className={styles.toggleText}>Yes</span>
                        </div>
                    </div>
                    
                    <div className={styles.buttonSection}>
                        <div className={styles.buttonWrapper}>
                            <button 
                                className={`${buttonStyles.YesButtonComponentStyling} ${styles.submitButton}`}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    'SUBMIT'
                                )}
                            </button>
                        </div>
                        <div className={styles.buttonWrapper}>
                            <button 
                                className={buttonStyles.NoButtonComponentStyling} 
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </FormikProvider>
            </div>
        </div>
    );
};

export default FeePropFeePopUp;
